import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FormValues } from "@src/components/dynamic-form/DynamicForm";
import {
  getSortedQuestion,
  resolveFirstUnanswered,
} from "@src/pages/quiz/quiz-screen/data.types";
import {
  Question,
  Response,
  Section,
  StudentAnswerApiResponse,
} from "@src/pages/student/data.type";

export const computeFiveSections = (
  rawSections: Section[],
  submitted: string[],
  dynamic: Record<number, string>,
  sequence: string,
): Section[] => {
  const seq = sequence || "AWA_VR1_QR1_VR2_QR2";
  const slots: Section[] = [];

  const findSection = (name: string) => {
    // 1. Check exact match by sectionName or sectionType
    let sec = rawSections.find(
      (s) => s.sectionName === name || s.sectionType === name,
    );
    // 2. VR1 / QR1 fallback to VR / QR
    if (!sec && name === "VR1") {
      sec = rawSections.find(
        (s) => s.sectionName === "VR" || s.sectionType === "VR",
      );
    }
    if (!sec && name === "QR1") {
      sec = rawSections.find(
        (s) => s.sectionName === "QR" || s.sectionType === "QR",
      );
    }
    // 3. Adaptive stage 2 fallback to VR2 / VR or QR2 / QR if specific VRE/VRM/VRH or QRE/QRM/QRH is not found
    if (!sec && (name === "VRM" || name === "VRE" || name === "VRH")) {
      sec = rawSections.find(
        (s) =>
          s.sectionName === "VR2" ||
          s.sectionType === "VR2" ||
          s.sectionType === "VR",
      );
    }
    if (!sec && (name === "QRM" || name === "QRE" || name === "QRH")) {
      sec = rawSections.find(
        (s) =>
          s.sectionName === "QR2" ||
          s.sectionType === "QR2" ||
          s.sectionType === "QR",
      );
    }
    return sec;
  };

  if (seq === "AWA_QR1_VR1_QR2_VR2") {
    const awa = findSection("AWA");
    if (awa) slots.push(awa);

    const qr1 = findSection("QR1");
    if (qr1) slots.push(qr1);

    const vr1 = findSection("VR1");
    if (vr1) slots.push(vr1);

    const qr2Name = dynamic[3] || "QRM";
    const qr2 = findSection(qr2Name);
    if (qr2) slots.push(qr2);

    const vr2Name = dynamic[4] || "VRM";
    const vr2 = findSection(vr2Name);
    if (vr2) slots.push(vr2);
  } else {
    const awa = findSection("AWA");
    if (awa) slots.push(awa);

    const vr1 = findSection("VR1");
    if (vr1) slots.push(vr1);

    const qr1 = findSection("QR1");
    if (qr1) slots.push(qr1);

    const vr2Name = dynamic[3] || "VRM";
    const vr2 = findSection(vr2Name);
    if (vr2) slots.push(vr2);

    const qr2Name = dynamic[4] || "QRM";
    const qr2 = findSection(qr2Name);
    if (qr2) slots.push(qr2);
  }

  return slots;
};

interface LegendDataType {
  answered: number;
  notAnswered: number;
  notVisited: number;
  marked: number;
}

export interface ResponseTestCase {
  input?: string;
  expectedOutput: string | Record<string, string>;
  actualOutput: string;
  result: boolean;
  error: string;
  testCaseType: "EASY" | "MEDIUM" | "HARD";
  testCaseScope?: "VISIBLE" | "HIDDEN";
  schemeSql?: string;
  seedSql?: string;
}

export interface CodeResultResponse {
  testCases: ResponseTestCase[];
  compilationError: string;
  questionId: string;
  versionId: string;
  studentId: string;
  labels: string[];
}
export interface StudentAnswers {
  [key: string]: Record<string, string>;
}

export interface InitialState {
  quizData: Response | null;
  rawSectionList: Section[] | null;
  submittedSections: string[];
  dynamicSections: Record<number, string>;
  studentSectionDataList: any[] | null;
  questionTimer: number;
  selectedQuestion: Question | null;
  quizLegendData: LegendDataType;
  activeSectionIndex: number;
  codeResult: CodeResultResponse | null;
  studentAnswers: StudentAnswerApiResponse["studentResponse"] | null;
  totalQuestionCount: number;
  malpracticeCount: {
    [studentId: string]: {
      [quizId: string]: number;
    };
  };

  showFullscreenWarning: boolean;
  showExitWarning: boolean;
  additionalDetailsFormValues: { name: string; values: FormValues | null };
  organizationData: {
    organization: { label: string; value: string };
    subOrganization: { label: string; value: string };
    branch: { label: string; value: string };
  };
}

const initialState: InitialState = {
  quizData: null,
  rawSectionList: null,
  submittedSections: [],
  dynamicSections: {},
  studentSectionDataList: null,
  questionTimer: 20,
  selectedQuestion: null,
  activeSectionIndex: 0,
  quizLegendData: {
    answered: 0,
    notAnswered: 0,
    marked: 0,
    notVisited: 0,
  },
  codeResult: null,
  studentAnswers: null,
  totalQuestionCount: 0,
  malpracticeCount: {},
  showFullscreenWarning: true,
  showExitWarning: false,
  additionalDetailsFormValues: { name: "", values: null },
  organizationData: {
    organization: { label: "", value: "" },
    subOrganization: { label: "", value: "" },
    branch: { label: "", value: "" },
  },
};

export const groupQuestionsByParagraphId = (questions: Question[] | any[]) => {
  if (!Array.isArray(questions) || questions.length === 0) return [];
  const paraGroups: Map<string, any[]> = new Map();
  const noParaQuestions: any[] = [];

  questions.forEach((q: any) => {
    const pid = q?.paragraphId || q?.version?.paragraphId;
    if (pid) {
      const key = String(pid);
      if (!paraGroups.has(key)) {
        paraGroups.set(key, []);
      }
      paraGroups.get(key)!.push(q);
    } else {
      noParaQuestions.push(q);
    }
  });

  const groupedList: any[] = [];
  paraGroups.forEach((group) => {
    groupedList.push(...group);
  });
  groupedList.push(...noParaQuestions);

  return groupedList;
};

const studentQuizSlice = createSlice({
  name: "studentQuiz",
  initialState,
  reducers: {
    setQuizData: (state, action) => {
      const quizCategoryType = action.payload.quizCategoryType || "";
      const sectionLabel =
        quizCategoryType === "assessment" ? "sectionList" : "quizSectionList";
      const quizType = action.payload.quizType || "";
      const tournamentSection = [
        { questionResponseList: action.payload.questionResponseList },
      ];
      const answerdQuestionIds = action.payload.answerdQuestionIds;
      const lastAttendedSectionName = action.payload.lastAttendedSectionName;
      const isTCSNQT =
        quizType?.toUpperCase() === "TCS_NQT" ||
        quizType?.toUpperCase() === "COMMUNICATION";

      let resumedSectionIdx = -1;
      if (isTCSNQT && lastAttendedSectionName) {
        const sectionsToCheck = action.payload?.[sectionLabel] || [];
        resumedSectionIdx = sectionsToCheck.findIndex(
          (sec: any) => sec.sectionName === lastAttendedSectionName,
        );
      }

      let newSectionList =
        quizCategoryType == "tournament"
          ? (tournamentSection.map((section, sectionIndex) => {
              const getQuestionList = (section: Section) => {
                const rawQuestions = section?.questionResponseList || [];
                const groupedQuestions =
                  groupQuestionsByParagraphId(rawQuestions);
                return groupedQuestions.map(
                  (question: Question, index: number) => {
                    const isAnswered =
                      answerdQuestionIds?.includes(question.id) || false;
                    const isPrevSection =
                      resumedSectionIdx !== -1 &&
                      sectionIndex < resumedSectionIdx;
                    return {
                      ...question,
                      answered: isAnswered || isPrevSection,
                      marked: false,
                      notAnswered:
                        answerdQuestionIds?.length > 0
                          ? false
                          : sectionIndex === 0 && index === 0,
                      notVisited:
                        answerdQuestionIds?.length > 0
                          ? !answerdQuestionIds?.includes(question.id)
                          : !(sectionIndex === 0 && index === 0),
                    };
                  },
                );
              };

              return {
                questionResponseList: getQuestionList(section as Section),
                sectionVisited: sectionIndex === 0,
              };
            }) as Section[])
          : (action.payload?.[sectionLabel].map(
              (section: Section, sectionIndex: number) => {
                const questionListLabel =
                  quizCategoryType === "assessment"
                    ? "questionResponseList"
                    : "questionList";

                const getQuestionList = (section: Section) => {
                  const rawQuestions = section?.[questionListLabel] || [];
                  const groupedQuestions =
                    groupQuestionsByParagraphId(rawQuestions);
                  return groupedQuestions.map(
                    (question, index) => {
                      const isAnswered =
                        answerdQuestionIds?.includes(question.id) || false;
                      const isPrevSection =
                        resumedSectionIdx !== -1 &&
                        sectionIndex < resumedSectionIdx;
                      return {
                        ...question,
                        answered: isAnswered || isPrevSection,
                        marked: false,
                        notAnswered:
                          action.payload.quizType === "PSYCHOMETRIC"
                            ? false
                            : isPrevSection
                              ? false
                              : answerdQuestionIds?.length > 0
                                ? false
                                : sectionIndex === 0 && index === 0,
                        notVisited:
                          action.payload.quizType === "PSYCHOMETRIC"
                            ? false
                            : isPrevSection
                              ? false
                              : answerdQuestionIds?.length > 0
                                ? !answerdQuestionIds?.includes(question.id)
                                : !(sectionIndex === 0 && index === 0),
                        answerStatus: isPrevSection ? "answered" : "",
                      };
                    },
                  );
                };

                const sortedQuestionList =
                  quizType === "PSYCHOMETRIC"
                    ? getSortedQuestion(
                        getQuestionList(section) ?? [],
                        quizType,
                      )
                    : [];

                const mappedQuestions = getQuestionList(section) || [];
                let adjustedDuration = section.sectionDuration || 0;

                if (isTCSNQT && sectionIndex === resumedSectionIdx) {
                  const answeredInThisSection = mappedQuestions.filter(
                    (q: any) => q.answered,
                  );
                  let totalAnsweredDuration = 0;
                  answeredInThisSection.forEach((q: any) => {
                    const durationType =
                      q.version?.questionDurationType || q.questionDurationType;
                    const durationVal = q.version?.duration || q.duration || 0;
                    const qDurationInSeconds =
                      durationType?.toLowerCase() === "seconds"
                        ? durationVal
                        : durationVal * 60;
                    totalAnsweredDuration += qDurationInSeconds;
                  });
                  adjustedDuration = Math.max(
                    0,
                    adjustedDuration - totalAnsweredDuration,
                  );
                }

                return {
                  ...section,
                  questionResponseList: mappedQuestions,
                  questionList: sortedQuestionList,
                  sectionVisited:
                    sectionIndex === 0 ||
                    (resumedSectionIdx !== -1 &&
                      sectionIndex <= resumedSectionIdx),
                  sectionDuration: adjustedDuration,
                };
              },
            ) as Section[]);
      const totalQuestions = newSectionList.reduce(
        (acc, sec) => acc + sec.questionResponseList.length,
        0,
      );

      let resolvedSectionIndex: number | undefined;
      let resolvedQuestion: Question | undefined;

      if (quizType === "PSYCHOMETRIC") {
        const resolved = resolveFirstUnanswered(newSectionList);

        if (resolved) {
          resolvedSectionIndex = resolved.sectionIndex;
          resolvedQuestion = resolved.question;

          const section = newSectionList[resolved.sectionIndex];

          const updatedQuestions = section.questionResponseList.map((q) => {
            if (q.id === resolved.question.id && !q.answered) {
              return {
                ...q,
                notVisited: false,
                notAnswered: true,
              };
            }
            return q;
          });

          if (!section.sectionVisited) {
            newSectionList[resolved.sectionIndex] = {
              ...section,
              questionResponseList: updatedQuestions,
              sectionVisited: true,
            };
          }
        }
      }

      const studentId = action.payload.studentId || "";
      const quizId = action.payload.quizId || "";
      if (quizType?.toUpperCase() === "GRE") {
        state.rawSectionList = newSectionList;
        const submittedStr = localStorage.getItem(
          `gre-${studentId}-${quizId}-submittedSections`,
        );
        const dynamicStr = localStorage.getItem(
          `gre-${studentId}-${quizId}-dynamicSections`,
        );
        const sectionDataListStr = localStorage.getItem(
          `gre-${studentId}-${quizId}-studentSectionDataList`,
        );

        state.submittedSections = submittedStr ? JSON.parse(submittedStr) : [];
        state.dynamicSections = dynamicStr ? JSON.parse(dynamicStr) : {};
        state.studentSectionDataList = sectionDataListStr
          ? JSON.parse(sectionDataListStr)
          : [];

        const computed = computeFiveSections(
          newSectionList,
          state.submittedSections,
          state.dynamicSections,
          action.payload.sectionSequence || "",
        );

        newSectionList = computed;
      }

      state.quizData = { ...action.payload, sectionList: newSectionList };
      const finalTotalQuestions = newSectionList.reduce(
        (acc, sec) => acc + sec.questionResponseList.length,
        0,
      );
      state.totalQuestionCount = finalTotalQuestions;

      if (quizType === "PSYCHOMETRIC") {
        const answeredCount = answerdQuestionIds?.length || 0;
        const hasUnanswered = answeredCount < finalTotalQuestions;

        state.quizLegendData = {
          answered: answeredCount,
          marked: 0,
          notAnswered: hasUnanswered ? 1 : 0,
          notVisited: hasUnanswered
            ? finalTotalQuestions - answeredCount - 1
            : 0,
        };
      } else {
        state.quizLegendData = {
          answered: answerdQuestionIds?.length || 0,
          marked: 0,
          notVisited:
            answerdQuestionIds?.length > 0
              ? newSectionList?.reduce(
                  (acc, sec) => acc + sec.questionResponseList.length,
                  0,
                ) - answerdQuestionIds?.length
              : newSectionList?.reduce(
                  (acc, sec) => acc + sec.questionResponseList.length,
                  0,
                ) - 1,
          notAnswered: answerdQuestionIds?.length > 0 ? 0 : 1,
        };
      }

      if (quizType === "PSYCHOMETRIC") {
        state.activeSectionIndex = resolvedSectionIndex ?? 0;
        state.selectedQuestion = resolvedQuestion ?? null;
      } else if (quizType === "GRE") {
        let activeIndex = 0;

        for (let i = 0; i < newSectionList.length; i++) {
          if (
            !state.submittedSections.includes(newSectionList[i].sectionName)
          ) {
            activeIndex = i;
            break;
          }
        }

        state.activeSectionIndex = activeIndex;

        const activeSec = newSectionList[activeIndex];
        state.selectedQuestion = activeSec?.questionResponseList?.[0] || null;
      } else {
        if (isTCSNQT && resumedSectionIdx !== -1) {
          state.activeSectionIndex = resumedSectionIdx;

          const resumedSection = newSectionList[resumedSectionIdx];
          const firstUnanswered = resumedSection?.questionResponseList?.find(
            (q: any) => !q.answered,
          );

          state.selectedQuestion = firstUnanswered
            ? firstUnanswered
            : resumedSection?.questionResponseList?.[
                resumedSection.questionResponseList.length - 1
              ] || null;
        } else {
          state.activeSectionIndex = 0;
          state.selectedQuestion =
            newSectionList && newSectionList[0]?.questionResponseList[0];
        }
      }
    },
    setQuestionTimer: (state, action) => {
      state.questionTimer = action.payload;
    },
    getLegendData: (state) => {
      state.quizLegendData = state.quizData?.sectionList?.reduce(
        (acc, section) => {
          (section.questionResponseList || []).forEach((question) => {
            acc.answered += question.answered ? 1 : 0;
            acc.notAnswered += question.notAnswered ? 1 : 0;
            acc.marked += question.marked ? 1 : 0;
            acc.notVisited += question.notVisited ? 1 : 0;
          });
          return acc;
        },
        { answered: 0, marked: 0, notVisited: 0, notAnswered: 0 },
      ) as LegendDataType;
    },

    setSelectedQuestion: (state, action: PayloadAction<Question>) => {
      const section = state.quizData?.sectionList[state.activeSectionIndex];

      if (!section) return;

      const selectedQuestionIndex = section.questionResponseList.findIndex(
        (q) => q.id === action.payload?.id,
      );
      if (selectedQuestionIndex === -1) return;

      const selectedQuestion =
        section.questionResponseList[selectedQuestionIndex];
      if (selectedQuestion.notVisited) {
        selectedQuestion.notVisited = false;
        selectedQuestion.notAnswered = true;
        state.quizLegendData.notVisited -= 1;
        state.quizLegendData.notAnswered += 1;
      }
      if (selectedQuestion.answered && selectedQuestion.notVisited === false) {
        selectedQuestion.notAnswered = false;
        state.quizLegendData.notAnswered -=
          state.quizLegendData.notAnswered > 0 ? 1 : 0;
      }

      selectedQuestion?.version?.testCases?.map((testCase) => ({
        ...testCase,
        disabled: true,
      }));

      state.selectedQuestion = selectedQuestion;
    },

    setPsychometricNextQuestion: (state) => {
      if (!state.quizData) return;

      const sections = state.quizData.sectionList;

      // --------------------------------------------------
      // 1️⃣ Resolve next unanswered question (forward only)
      // --------------------------------------------------
      const resolved = resolveFirstUnanswered(
        sections,
        state.activeSectionIndex,
      );

      if (!resolved) return;

      const { sectionIndex } = resolved;
      const section = sections[sectionIndex];

      const questionIndex = section.questionResponseList.findIndex(
        (q) => q.id === resolved.question.id,
      );
      if (questionIndex === -1) return;

      const nextQuestion = section.questionResponseList[questionIndex];

      // --------------------------------------------------
      // 2️⃣ Clear any previous notAnswered (safety invariant)
      // --------------------------------------------------
      sections.forEach((sec) => {
        sec.questionResponseList.forEach((q) => {
          if (q.notAnswered) q.notAnswered = false;
        });
      });

      // --------------------------------------------------
      // 3️⃣ Activate the next question
      // --------------------------------------------------
      nextQuestion.notVisited = false;
      nextQuestion.notAnswered = true;
      nextQuestion.answered = false;

      // --------------------------------------------------
      // 4️⃣ Update legends deterministically
      // --------------------------------------------------
      state.quizLegendData.notAnswered = 1;
      state.quizLegendData.notVisited =
        state.totalQuestionCount - state.quizLegendData.answered - 1;

      // --------------------------------------------------
      // 5️⃣ Update navigation state
      // --------------------------------------------------
      state.activeSectionIndex = sectionIndex;
      state.selectedQuestion = nextQuestion;

      // --------------------------------------------------
      // 6️⃣ Mark section visited
      // --------------------------------------------------
      if (!section.sectionVisited) {
        state.quizData.sectionList[sectionIndex] = {
          ...section,
          sectionVisited: true,
        };
      }
    },

    setActiveSectionIndex: (state, action: PayloadAction<number>) => {
      if (!state.quizData) return;
      state.activeSectionIndex = action.payload;
      const section = state.quizData?.sectionList[action.payload];
      if (!section) return;

      const firstQuestion =
        section.questionResponseList?.[0] || section.questionList?.[0];
      state.selectedQuestion = firstQuestion;
      if (section.sectionVisited) return;

      if (firstQuestion) {
        state.quizLegendData.notVisited -= firstQuestion.notVisited ? 1 : 0;
        state.quizLegendData.notAnswered += firstQuestion.notAnswered ? 0 : 1;
        firstQuestion.notVisited = false;
        firstQuestion.notAnswered = true;
      }

      state.quizData.sectionList[action.payload] = {
        ...section,
        sectionVisited: true,
      };
    },

    setPsychoActiveSectionIndex: (state, action: PayloadAction<number>) => {
      if (!state.quizData) return;

      const sections = state.quizData.sectionList;
      const totalSections = sections.length;

      let resolvedSectionIndex = action.payload;
      let resolvedQuestion: Question | undefined;

      // 1️⃣ Traverse sections starting from current index
      for (let i = action.payload; i < totalSections; i++) {
        const section = sections[i];
        if (!section) continue;

        const unansweredQuestion = section.questionResponseList.find(
          (q) => q.answered !== true,
        );

        if (unansweredQuestion) {
          resolvedSectionIndex = i;
          resolvedQuestion = unansweredQuestion;
          break;
        }
      }

      // 2️⃣ Fallback: all questions answered in all remaining sections
      if (!resolvedQuestion) {
        const lastSection = sections[totalSections - 1];
        if (!lastSection || lastSection.questionResponseList.length === 0)
          return;

        resolvedSectionIndex = totalSections - 1;
        resolvedQuestion =
          lastSection.questionResponseList[
            lastSection.questionResponseList.length - 1
          ];
      }

      // 3️⃣ Apply resolved navigation
      state.activeSectionIndex = resolvedSectionIndex;
      state.selectedQuestion = resolvedQuestion;

      const resolvedSection = sections[resolvedSectionIndex];

      // 4️⃣ Legend updates ONLY if section was not visited before
      if (!resolvedSection.sectionVisited) {
        state.quizLegendData.notVisited -= resolvedQuestion.notVisited ? 1 : 0;
        state.quizLegendData.notAnswered += resolvedQuestion.notAnswered
          ? 0
          : 1;

        resolvedQuestion.notVisited = false;

        state.quizData.sectionList[resolvedSectionIndex] = {
          ...resolvedSection,
          sectionVisited: true,
        };
      }
    },

    setMarked: (state) => {
      if (!state.selectedQuestion) return;
      const section = state.quizData?.sectionList[state.activeSectionIndex];
      if (!section) return;

      const questionIndex = section.questionResponseList?.findIndex(
        (q) => q.id === state.selectedQuestion?.id,
      );
      if (questionIndex === -1) return;

      const question = section.questionResponseList[questionIndex];
      question.marked = !question.marked;

      state.quizLegendData.marked += question.marked ? 1 : -1;

      state.selectedQuestion = { ...question };
    },
    addTestCase: (state) => {
      const testCases = state.selectedQuestion?.version.testCases;
      if (Array.isArray(testCases) && testCases.length > 0) {
        testCases.push({ ...testCases[testCases.length - 1], disabled: false });
      }
    },
    changeTestCaseInput: (state, { payload }) => {
      const testCases = state.selectedQuestion?.version.testCases || [];
      testCases[payload.selectedTestCase].input[payload.selectedInput] =
        payload.value;
    },
    setCodeResult: (state, action) => {
      state.codeResult = action.payload;
    },
    setQuestionAnswered: (state, action) => {
      const section = state?.quizData?.sectionList
        ? state?.quizData?.sectionList[state.activeSectionIndex]
        : undefined;
      if (!section) return;
      const questionIndex = section?.questionResponseList?.findIndex(
        (q) => q.id === action.payload.id,
      ) as number;
      if (questionIndex === -1) return;

      const question = section?.questionResponseList[questionIndex];
      if (!question?.answered) {
        question.answered = true;
        question.notAnswered = false;

        state.quizLegendData.answered += question?.answered ? 1 : -1;
        state.quizLegendData.notAnswered = state.quizLegendData.notAnswered - 1;
      }
      question.answerStatus = action.payload.answerStatus || "";
    },

    setPsychometricQuestionAnswered: (state, action) => {
      const section = state.quizData?.sectionList?.[state.activeSectionIndex];
      if (!section) return;

      const questionIndex = section.questionResponseList.findIndex(
        (q) => q.id === action.payload.id,
      );
      if (questionIndex === -1) return;

      const question = section.questionResponseList[questionIndex];

      if (question.answered) return;
      question.answered = true;
      question.notVisited = false;
      question.notAnswered = false;
      question.answerStatus = action.payload.answerStatus || "";
      state.quizLegendData.answered += 1;

      if (state.quizLegendData.notAnswered > 0) {
        state.quizLegendData.notAnswered -= 1;
      }
    },
    setInitialStudentAnswers: (state, action) => {
      state.studentAnswers = { ...state.studentAnswers, ...action.payload };
    },
    setStudentAnswers: (state, action) => {
      state.studentAnswers = action.payload;
    },
    clearQuizDataOnSubmit: (state) => {
      state.quizData = null;
      state.rawSectionList = null;
      state.submittedSections = [];
      state.dynamicSections = {};
      state.studentSectionDataList = null;
      state.selectedQuestion = null;
      state.activeSectionIndex = 0;
      state.codeResult = null;
      state.quizLegendData = {
        answered: 0,
        notAnswered: 0,
        marked: 0,
        notVisited: 0,
      };
      state.studentAnswers = null;
      state.totalQuestionCount = 0;
    },
    submitSectionSuccess: (
      state,
      action: PayloadAction<{
        sectionName: string;
        studentSectionDataList: any[];
        studentId: string;
        quizId: string;
      }>,
    ) => {
      const { sectionName, studentSectionDataList, studentId, quizId } =
        action.payload;

      if (!state.submittedSections.includes(sectionName)) {
        state.submittedSections.push(sectionName);
      }
      state.studentSectionDataList = studentSectionDataList;

      // Save to localStorage
      localStorage.setItem(
        `gre-${studentId}-${quizId}-submittedSections`,
        JSON.stringify(state.submittedSections),
      );
      localStorage.setItem(
        `gre-${studentId}-${quizId}-studentSectionDataList`,
        JSON.stringify(studentSectionDataList),
      );

      const seq = state.quizData?.sectionSequence || "AWA_VR1_QR1_VR2_QR2";

      const getStage2Name = (stage1Name: "VR1" | "QR1", type: "VR" | "QR") => {
        const stage1Data = studentSectionDataList.find(
          (s) =>
            s.sectionName === stage1Name ||
            s.sectionType === stage1Name ||
            (stage1Name === "VR1" &&
              (s.sectionName === "VR" || s.sectionType === "VR")) ||
            (stage1Name === "QR1" &&
              (s.sectionName === "QR" || s.sectionType === "QR")),
        );
        const score =
          stage1Data &&
          stage1Data.passedQuestions !== undefined &&
          stage1Data.passedQuestions !== null
            ? stage1Data.passedQuestions
            : 0;

        const stage1Section = state.rawSectionList?.find(
          (s) =>
            s.sectionName === stage1Name ||
            s.sectionType === stage1Name ||
            (stage1Name === "VR1" &&
              (s.sectionName === "VR" || s.sectionType === "VR")) ||
            (stage1Name === "QR1" &&
              (s.sectionName === "QR" || s.sectionType === "QR")),
        );
        const hardCutoff = stage1Section?.hard ?? 9;
        const mediumCutoff = stage1Section?.medium ?? 7;

        if (score >= hardCutoff) {
          return type === "VR" ? "VRH" : "QRH";
        } else if (score >= mediumCutoff) {
          return type === "VR" ? "VRM" : "QRM";
        } else {
          return type === "VR" ? "VRE" : "QRE";
        }
      };

      const isVR1 = sectionName === "VR1" || sectionName === "VR";
      const isQR1 = sectionName === "QR1" || sectionName === "QR";

      if (seq === "AWA_QR1_VR1_QR2_VR2") {
        if (isQR1) {
          state.dynamicSections[3] = getStage2Name("QR1", "QR");
        } else if (isVR1) {
          state.dynamicSections[4] = getStage2Name("VR1", "VR");
        }
      } else {
        if (isVR1) {
          state.dynamicSections[3] = getStage2Name("VR1", "VR");
        } else if (isQR1) {
          state.dynamicSections[4] = getStage2Name("QR1", "QR");
        }
      }

      localStorage.setItem(
        `gre-${studentId}-${quizId}-dynamicSections`,
        JSON.stringify(state.dynamicSections),
      );

      if (state.rawSectionList && state.quizData) {
        const computed = computeFiveSections(
          state.rawSectionList,
          state.submittedSections,
          state.dynamicSections,
          state.quizData.sectionSequence || "",
        );
        state.quizData.sectionList = computed;
      }
    },
    initializeMalpracticeCount: (
      state,
      action: PayloadAction<{
        studentId: string;
        quizId: string;
        studentMalPracticeCount: number | string | null | undefined;
      }>,
    ) => {
      const { studentId, quizId, studentMalPracticeCount } = action.payload;

      if (!state.malpracticeCount[studentId]) {
        state.malpracticeCount[studentId] = {};
      }
      state.malpracticeCount[studentId][quizId] =
        studentMalPracticeCount == null ? 0 : Number(studentMalPracticeCount) || 0;
    },

    incrementMalpracticeCount: (
      state,
      action: PayloadAction<{ studentId: string; quizId: string }>,
    ) => {
      const { studentId, quizId } = action.payload;

      if (!state.malpracticeCount[studentId]) {
        state.malpracticeCount[studentId] = {};
      }
      if (!state.malpracticeCount[studentId][quizId]) {
        state.malpracticeCount[studentId][quizId] = 0;
      }
      state.malpracticeCount[studentId][quizId] += 1;
    },

    resetMalpracticeCount: (
      state,
      action: PayloadAction<{ studentId: string; quizId: string }>,
    ) => {
      const { studentId, quizId } = action.payload;

      if (state.malpracticeCount[studentId]) {
        state.malpracticeCount[studentId][quizId] = 0;
      }
    },

    setShowFullscreenWarning: (state, action) => {
      state.showFullscreenWarning = action.payload;
    },
    setShowExitWarning: (state, action) => {
      state.showExitWarning = action.payload;
    },
    setAdditionalDetailsFormValues: (
      state,
      action: PayloadAction<{ name: string; values: FormValues | null }>,
    ) => {
      state.additionalDetailsFormValues = action.payload;
    },
    setOrganizationData: (state, action) => {
      state.organizationData = { ...state.organizationData, ...action.payload };
    },
  },
});

export const {
  setQuizData,
  setQuestionTimer,
  getLegendData,
  setSelectedQuestion,
  setActiveSectionIndex,
  setMarked,
  addTestCase,
  changeTestCaseInput,
  setCodeResult,
  setQuestionAnswered,
  setStudentAnswers,
  setInitialStudentAnswers,
  clearQuizDataOnSubmit,
  initializeMalpracticeCount,
  incrementMalpracticeCount,
  resetMalpracticeCount,
  setShowExitWarning,
  setShowFullscreenWarning,
  setAdditionalDetailsFormValues,
  setOrganizationData,
  setPsychoActiveSectionIndex,
  setPsychometricQuestionAnswered,
  setPsychometricNextQuestion,
  submitSectionSuccess,
} = studentQuizSlice.actions;
export default studentQuizSlice.reducer;
